import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { routes } from './App';
import { installChunkReload } from './chunkReload';
import { theme } from './theme';
import { queryClient } from './queryClient';

import './fonts.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';
// DLP layer (#117): @media print neutering, .regulated-field user-select.
import './dlp/dlp.css';
// App-wide component style tweaks (non-font globals).
import './app.css';

// Task #251 (2026-06-04): DataRouter migration. Replaces <BrowserRouter>
// + <App /> + JSX routes with createBrowserRouter() driven by the route
// object array exported from App.tsx. Two reasons:
//   1. Unlocks useBlocker — the dirty-form-guard's back/forward intercept
//      (deferred at #100 due to render-loop freezes under the legacy
//      <BrowserRouter>). Stage 2 wires this up in useDirtyFormGuard.
//   2. Future-proofs the app for v6.4+ DataRouter capabilities (loaders,
//      actions, error boundaries, defer).
//
// Provider hierarchy change: DirtyFormProvider, AlertModalProvider, and
// <Notifications /> moved INTO the route tree (RootLayout in App.tsx)
// because useBlocker requires its callers to live inside the router
// context. The Mantine + QueryClient providers stay outside the router
// because they're context independent.
//
// future flags carried over from the previous <BrowserRouter future={...}>:
//   - v7_startTransition: state updates wrapped in React.startTransition
//     (this flag lives on <RouterProvider> in v6.x, not createBrowserRouter)
//   - v7_relativeSplatPath: relative path resolution inside splat routes
//     (this flag lives on createBrowserRouter options).
// P33: reload once when a lazy chunk from a previous build is gone (see chunkReload.ts).
installChunkReload();

const router = createBrowserRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
